import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ChevronLeft, ChevronRight, Star } from 'lucide-react';

/**
 * Vintage cassette-tape styled Featured rotator.
 * Two spinning reels, a paper-style label, and the playlist cover
 * peeking through the label window. Auto-advances every 7s.
 */
const CassetteFeatured = ({ items }) => {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => setIdx((i) => (i + 1) % items.length), 7000);
    return () => clearInterval(timerRef.current);
  }, [items.length]);

  const goTo = (i) => {
    setIdx(((i % items.length) + items.length) % items.length);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setIdx((k) => (k + 1) % items.length), 7000);
  };

  const current = items[idx];
  if (!current) return null;

  return (
    <section className="mt-8 relative" data-testid="featured-rotator">
      <Link
        to={`/playlist/${current.slug}`}
        data-testid={`featured-slide-${current.slug}`}
        className="block relative rounded-[22px] overflow-hidden border border-[#3D1A1A] group"
        aria-label={`Play ${current.name}`}
      >
        {/* Cassette body — deep maroon plastic with subtle gold plate on top */}
        <div className="relative aspect-[16/9] sm:aspect-[16/7] bg-gradient-to-b from-[#2A0F0F] via-[#1A0808] to-[#0E0303]">
          {/* Cover image ghost through the label window */}
          {items.map((it, i) => (
            <img
              key={it.id}
              src={it.cover_image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              style={{ opacity: i === idx ? 0.30 : 0 }}
              loading={i === 0 ? 'eager' : 'lazy'}
            />
          ))}

          {/* Warm gold vignette + cassette highlight */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#FFB800]/10 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-black/20 to-transparent pointer-events-none" />

          {/* Screws in corners for that hardware feel */}
          <Screw pos="top-3 left-3" />
          <Screw pos="top-3 right-3" />
          <Screw pos="bottom-3 left-3" />
          <Screw pos="bottom-3 right-3" />

          {/* Reels + label + tape span the middle */}
          <div className="absolute inset-0 flex items-center gap-3 sm:gap-6 px-8 sm:px-14">
            <Reel spinning />
            <div className="flex-1 relative h-full flex items-center">
              {/* Paper label */}
              <div className="w-full rounded-md bg-gradient-to-br from-[#F4E1B7] via-[#E8D2A0] to-[#C9A97A] p-3 sm:p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.4),0_2px_8px_rgba(0,0,0,0.5)] border border-[#8A6B3E]/40 relative">
                <div className="text-[9px] uppercase tracking-[0.3em] text-[#8A3F1E] font-bold flex items-center gap-1">
                  <Star className="w-2.5 h-2.5 fill-current" />
                  Side A · Chuninda
                </div>
                <div className="mt-0.5 font-display text-[#2A0F0F] text-lg sm:text-2xl leading-tight truncate">
                  {current.name}
                </div>
                <div className="text-[10px] sm:text-xs text-[#5A2418] mt-0.5 truncate">
                  {current.description || `${current.song_ids?.length || 0} songs · Patna Ke Presleys`}
                </div>
                {/* Faint horizontal ruled lines like a real inlay */}
                <div className="absolute inset-x-3 bottom-2 h-px bg-[#8A6B3E]/30" />
              </div>
            </div>
            <Reel spinning reverse />
          </div>

          {/* Tape strip stretching under reels */}
          <div className="absolute left-8 right-8 sm:left-14 sm:right-14 bottom-[38%] h-1 bg-gradient-to-r from-transparent via-[#2A0F0F] to-transparent opacity-70 pointer-events-none" />

          {/* Play affordance in corner */}
          <div className="absolute bottom-4 right-6 w-11 h-11 rounded-full bg-[#FFB800] text-[#120505] flex items-center justify-center shadow-[0_6px_20px_rgba(255,184,0,0.5)]">
            <Play className="w-5 h-5 fill-current ml-0.5" />
          </div>
        </div>
      </Link>

      {items.length > 1 && (
        <>
          <button
            data-testid="featured-prev"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); goTo(idx - 1); }}
            className="hidden sm:flex absolute -left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#120505]/80 backdrop-blur border border-[#3D1A1A] items-center justify-center text-[#FFF3E0] hover:bg-[#220A0A]"
            aria-label="Previous featured"
          ><ChevronLeft className="w-4 h-4" /></button>
          <button
            data-testid="featured-next"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); goTo(idx + 1); }}
            className="hidden sm:flex absolute -right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#120505]/80 backdrop-blur border border-[#3D1A1A] items-center justify-center text-[#FFF3E0] hover:bg-[#220A0A]"
            aria-label="Next featured"
          ><ChevronRight className="w-4 h-4" /></button>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {items.map((_, i) => (
              <button
                key={i}
                data-testid={`featured-dot-${i}`}
                onClick={() => goTo(i)}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-[#FFB800]' : 'w-1.5 bg-[#3D1A1A] hover:bg-[#BFA5A5]'}`}
                aria-label={`Cassette ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
};

const Screw = ({ pos }) => (
  <div className={`absolute ${pos} w-2 h-2 rounded-full bg-gradient-to-br from-[#3D1A1A] to-[#0E0303] border border-[#FFB800]/20 shadow-inner`} />
);

const Reel = ({ spinning = false, reverse = false }) => (
  <div className="relative shrink-0 w-16 h-16 sm:w-20 sm:h-20">
    {/* Dark outer ring */}
    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#0E0303] via-[#2A0F0F] to-[#0E0303] border border-[#FFB800]/25 shadow-[inset_0_2px_4px_rgba(0,0,0,0.6)]" />
    {/* Rotating spokes */}
    <div
      className={`absolute inset-2 rounded-full flex items-center justify-center ${spinning ? 'vinyl-spin' : ''}`}
      style={reverse ? { animationDirection: 'reverse' } : undefined}
    >
      <svg viewBox="0 0 40 40" className="w-full h-full">
        <circle cx="20" cy="20" r="18" fill="none" stroke="#FFB800" strokeOpacity="0.15" strokeWidth="1" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <line
            key={deg}
            x1="20" y1="6" x2="20" y2="16"
            stroke="#FFB800" strokeOpacity="0.55" strokeWidth="1.4" strokeLinecap="round"
            transform={`rotate(${deg} 20 20)`}
          />
        ))}
        <circle cx="20" cy="20" r="4" fill="#FFB800" opacity="0.75" />
        <circle cx="20" cy="20" r="1.3" fill="#120505" />
      </svg>
    </div>
  </div>
);

export default CassetteFeatured;
